<script setup lang="ts">
import { provide, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { NxtSpinner, NxtIcon, NxtIconButton, NxtDrawer, NxtButton } from '@future-harry/nxt-styles';
import { useJobBoard } from '@/core/composables/useJobBoard';
import { useLiveEditor } from '@/modules/live-editor/composables/live-editor.composable';
import { useJobBoardSettings } from '@/apps/admin/composables/job-board-settings.composable';
import Settings from '@/apps/admin/components/settings/Settings.vue';
import LiveEditor from '@/modules/live-editor/components/LiveEditor.vue';
import JobBoardByIdSkeleton from '@/apps/admin/components/skeletons/pages/JobBoardByIdSkeleton.vue';
import { useAppStore } from '@/core/collections/app.store';

const route = useRoute();
const id = route.params.id as string;
const viewport = ref<'desktop' | 'tablet' | 'mobile'>('desktop');

provide('pageId', id);
provide('viewport', viewport);

const appStore = useAppStore();
const { isJobBoardFetchFinished, themeStyles, currentJobBoard, isUpdatingJobBoard, updateJobBoard, fetchJobBoard } = useJobBoard(id);
const { isSettingsOpen, openSettings, closeSettings, validate } = useJobBoardSettings(currentJobBoard);
const { isDirty, isSavingChanges, discardChanges, saveChanges } = useLiveEditor(currentJobBoard);

onBeforeMount(() => {
   appStore.setBreadcrumbs([
      {
         content: 'Jobsites',
         to: '/',
      },
      {
         content: '',
         to: `/${id}`,
      },
   ]);
});

onMounted(async () => {
   await fetchJobBoard();

   appStore.setBreadcrumbs([
      {
         content: 'Jobsites',
         to: '/',
      },
      {
         content: currentJobBoard.value?.name,
         to: `/${id}`,
      },
   ]);
});

const setViewport = (v: 'desktop' | 'tablet' | 'mobile') => {
   viewport.value = v;
};

const handleSettingsClose = () => {
   closeSettings();
};

const saveSettings = async () => {
   if (!validate()) return;
   await updateJobBoard();
};

const saveLiveEditor = async () => {
   await saveChanges();
};
</script>

<template>
   <JobBoardByIdSkeleton v-if="!isJobBoardFetchFinished" />
   <div v-else class="jobboard-detail" :style="themeStyles">
      <Teleport to=".page-header__bottom">
         <div class="page-header-actions">
            <div></div>
            <div class="page-header-actions__center">
               <NxtIconButton :class="{ 'is-active': viewport === 'desktop' }" name="desktop" @click="setViewport('desktop')" />
               <NxtIconButton :class="{ 'is-active': viewport === 'tablet' }" name="tablet-screen" @click="setViewport('tablet')" />
               <NxtIconButton :class="{ 'is-active': viewport === 'mobile' }" name="mobile-screen" @click="setViewport('mobile')" />
            </div>
            <div class="page-header-actions__right">
               <NxtButton class="page-header-actions__icon-button" :disabled="!isDirty" :button-type="'grey'" @click="discardChanges">
                  <NxtIcon name="history" size="small" />
                  <span>{{ $t('live-editor.discard_changes') }}</span>
               </NxtButton>
               <NxtButton
                  class="page-header-actions__icon-button"
                  :disabled="!isDirty || isSavingChanges"
                  :button-type="'primary'"
                  @click="saveLiveEditor"
               >
                  <template v-if="isSavingChanges">
                     <NxtSpinner size="tiny" style="color: white" />
                  </template>
                  <template v-else>
                     <NxtIcon name="save" color="var(--nxt-light)" size="small" />
                     <span>{{ $t('live-editor.save_changes') }}</span>
                  </template>
               </NxtButton>
               <NxtIconButton name="gears" @click="openSettings" />
            </div>
         </div>
      </Teleport>

      <LiveEditor />

      <NxtDrawer
         v-if="isSettingsOpen"
         :title="$t('job-board.settings.title')"
         :position="'right'"
         :width="'90rem'"
         :has-close-button="true"
         @on-close="handleSettingsClose"
      >
         <Settings v-model="currentJobBoard" />
         <template #footer>
            <div class="footer-actions">
               <NxtButton @click="saveSettings" :disabled="isUpdatingJobBoard">
                  <span v-if="!isUpdatingJobBoard">{{ $t('job-board.settings.save') }}</span>
                  <NxtSpinner v-else size="tiny" style="color: white" />
               </NxtButton>
            </div>
         </template>
      </NxtDrawer>
   </div>
</template>

<style lang="scss" scoped>
.jobboard-detail {
   height: 100%;
   width: 100%;
   padding-bottom: var(--nxt-gutter);

   :deep(.nxt-drawer) {
      overflow: hidden;

      .nxt-drawer__main {
         overflow-y: scroll;
      }
   }
}

.page-header-actions {
   display: flex;
   align-items: flex-end;
   gap: var(--nxt-gutter);
   justify-content: space-between;

   &__icon-button {
      display: flex;
      align-items: center;
      gap: var(--nxt-gutter-small);

      span {
         line-height: var(--nxt-button-height);
      }
   }

   &__center {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--nxt-gutter-small);
      flex: 1 1 auto;

      .is-active {
         background-color: var(--nxt-light-grey);
      }
   }

   > div:first-child {
      flex: 1 1 auto;
   }

   &__right {
      display: flex;
      align-items: flex-end;
      gap: var(--nxt-gutter);
      justify-content: flex-end;
   }
}

.footer-actions {
   padding: var(--nxt-gutter-large);
   display: flex;
   align-content: flex-end;
   justify-content: flex-end;
}

:deep(.nxt-drawer__footer) {
   background-color: var(--nxt-extralight-grey);
}
</style>
